import {
  Home,
  Users,
  Settings,
  Shield,
  BarChart3,
  Table,
  Package,
  Lock,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Search,
  Activity,
  Ticket,
  Server,
  Workflow,
  Building2,
  ShoppingCart,
  BookOpen,
  GraduationCap,
  FileText,
  Network,
  ShieldAlert,
  LayoutDashboard,
} from "lucide-react";
import { MODULES } from "@repo/permissions";
import { attachDefinitionHubChildren, attachHeaderTabHubChildren } from "./sidebar-nav.hubs.js";

/** @typedef {'quickAccess'|'governance'|'riskAssets'|'compliance'|'resilience'|'privacy'|'serviceManagement'|'definitions'|'administration'|'help'} SidebarSectionId */

export const SIDEBAR_MODULE_OPTIONS = [
  {
    key: MODULES.CORE,
    defaultLabel: "Core Platform",
    icon: Home,
    featuredIds: ["user-list", "role", "workflow-templates", "third-party-applications", "party-templates", "about-sgrc"],
  },
  {
    key: MODULES.ISO27001,
    defaultLabel: "ISO 27001",
    icon: Shield,
    featuredIds: ["oc-context", "oc-scope-document", "controls", "soa", "objective", "oc-interested-parties"],
  },
  {
    key: MODULES.TRAINING,
    defaultLabel: "Training",
    icon: GraduationCap,
    featuredIds: ["list", "myTraining", "competence"],
  },
  {
    key: MODULES.SCF,
    defaultLabel: "Secure Control Framework (SCF)",
    icon: CheckCircle,
    featuredIds: ["controls", "threat-intelligence", "vulnerability"],
  },
  {
    key: MODULES.KVKK,
    defaultLabel: "KVKK",
    icon: Lock,
    featuredIds: ["pd-inventory", "verbis", "disposal", "obtaining-explicit-consent", "application-tracking", "data-breach"],
  },
  {
    key: MODULES.GDPR,
    defaultLabel: "GDPR",
    icon: Shield,
    featuredIds: ["gdpr-dashboard", "pd-inventory", "dpia-assessment", "privacy-notice", "data-subject-request", "dpa-registry"],
  },
  {
    key: MODULES.BCM,
    defaultLabel: "Business Continuity (BCM)",
    icon: Activity,
    featuredIds: ["bia", "bcp", "crisis-event", "exercises"],
  },
  {
    key: MODULES.BIGDES,
    defaultLabel: "BIGDES",
    icon: Table,
    featuredIds: ["measure-applicability", "current-situation-analysis", "road-map", "compensatory-control", "asset-group-evaluation", "audit-reports"],
  },
  {
    key: MODULES.RISK,
    defaultLabel: "Risk Management",
    icon: AlertTriangle,
    featuredIds: ["risk-register", "risk-treatment", "risk-monitoring", "residual-risk-acceptance", "risk-opportunities"],
  },
  {
    key: MODULES.ASSET,
    defaultLabel: "Asset Management",
    icon: Package,
    featuredIds: ["assets", "asset-group", "categories", "maintenance-management", "vendor-registry", "license-management"],
  },
  {
    key: MODULES.AUDIT,
    defaultLabel: "Audit",
    icon: Search,
    featuredIds: ["kpi-dashboard", "audit-calendar", "assessment", "my-audits", "capa", "assessment-questions"],
  },
  {
    key: MODULES.TICKETING,
    defaultLabel: "Ticketing",
    icon: Ticket,
    featuredIds: ["ticket-list", "ticket-my-tasks", "ticket-dashboard", "itsm-service-catalog", "itsm-change-request", "itsm-problem"],
  },
  {
    key: MODULES.DOCUMENT,
    defaultLabel: "Document Management",
    icon: FileText,
    featuredIds: ["report-document", "document-type", "classification"],
  },
  {
    key: MODULES.INCIDENT,
    defaultLabel: "Incident Management",
    icon: AlertCircle,
    featuredIds: ["incidents", "incident-monitoring", "actions", "incident-templates"],
  },
  {
    key: MODULES.DIRECTORY,
    defaultLabel: "Directory Management",
    icon: Network,
    featuredIds: ["tenant-directory"],
  },
];

/**
 * @param {(key: string, defaultValue?: string) => string} t
 */
export function createSidebarSections(t) {
  return [
    { id: "quickAccess", label: t("sections.quickAccess", { defaultValue: "Quick Access" }) },
    { id: "governance", label: t("sections.governance", { defaultValue: "Governance" }) },
    { id: "riskAssets", label: t("sections.riskAssets", { defaultValue: "Risk and Assets" }) },
    { id: "compliance", label: t("sections.compliance", { defaultValue: "Compliance and Audit" }) },
    { id: "resilience", label: t("sections.resilience", { defaultValue: "Incident and Continuity" }) },
    { id: "privacy", label: t("sections.privacy", { defaultValue: "Privacy" }) },
    { id: "serviceManagement", label: t("sections.serviceManagement", { defaultValue: "Service Management" }) },
    { id: "definitions", label: t("sections.definitions", { defaultValue: "Definitions" }) },
    { id: "administration", label: t("sections.administration", { defaultValue: "Platform & Admin" }) },
    { id: "help", label: t("sections.help", { defaultValue: "Help" }) },
  ];
}

/**
 * @param {(key: string, options?: object) => string} t
 * @param {{ tNav?: Function, tBreadcrumb?: Function, tMatrix?: Function }} [hubTranslators]
 */
export function createNavData(t, hubTranslators) {
  const data = {
    sections: createSidebarSections(t),
    navMain: [
      {
        id: "home",
        section: "quickAccess",
        title: t("nav.home"),
        url: "/home",
        icon: Home,
        helperKey: "home",
        pageKey: "home",
      },
      {
        id: "my-work",
        section: "quickAccess",
        title: t("nav.myWork", { defaultValue: "My Work" }),
        url: "/my-work",
        icon: CheckCircle,
        helperKey: "myWork",
        pageKey: "myWork",
      },
      {
        id: "dashboard",
        section: "quickAccess",
        title: t("nav.dashboard", { defaultValue: "Dashboard" }),
        url: "/dashboard",
        icon: LayoutDashboard,
        helperKey: "dashboard",
        pageKey: "home",
      },
      {
        id: "organizational-context",
        section: "governance",
        title: t("nav.organizationalContext.title"),
        url: "#",
        icon: Building2,
        helperKey: "organizationalContext",
        focusModules: [MODULES.ISO27001],
        items: [
          { id: "oc-context", title: t("nav.organizationalContext.context"), url: "/context-management/context" },
          { id: "oc-interested-parties", title: t("nav.organizationalContext.interestedParties"), url: "/context-management/interested-parties" },
          { id: "oc-scope-document", title: t("nav.organizationalContext.scopeDocument"), url: "/context-management/scope-document" },
          { id: "objective", title: t("nav.organizationalContext.objectives"), url: "/isms-controls/objective" },
          { id: "oc-communication", title: t("nav.organizationalContext.communication"), url: "/context-management/communication" },
          { id: "oc-meeting", title: t("nav.organizationalContext.meeting"), url: "/context-management/meeting" },
        ],
      },
      {
        id: "documents-forms",
        section: "governance",
        title: t("nav.documentsForms.title", { defaultValue: "Documents and Forms" }),
        url: "#",
        icon: FileText,
        helperKey: "documentsForms",
        items: [
          { id: "report-document", title: t("nav.reports.document"), url: "/report-operations/document", pageKey: "reportDocument", partyEntityType: "Document", focusModules: [MODULES.DOCUMENT] },
          { id: "forms", title: t("nav.reports.forms"), url: "/report-operations/forms", pageKey: "reportForms" },
        ],
      },
      {
        id: "training",
        section: "governance",
        title: t("nav.training.title", { defaultValue: "Training and Competence" }),
        url: "#",
        icon: GraduationCap,
        helperKey: "training",
        focusModules: [MODULES.TRAINING],
        items: [
          { id: "list", title: t("nav.training.list", { defaultValue: "Trainings" }), url: "/training/list", pageKey: "isoTraining" },
          { id: "myTraining", title: t("nav.training.myTraining", { defaultValue: "My Trainings" }), url: "/training/my-training", pageKey: "isoMyTraining" },
          { id: "competence", title: t("nav.training.competence", { defaultValue: "Competence" }), url: "/training/competence", pageKey: "isoCompetence" },
        ],
      },
      {
        id: "reports",
        section: "governance",
        title: t("nav.reports.title"),
        url: "#",
        icon: BarChart3,
        helperKey: "reports",
        items: [
          { id: "report-studio", title: t("nav.reports.studio"), url: "/reports/studio", pageKey: "reportReports" },
          { id: "report-distributions", title: t("nav.reports.distributions"), url: "/reports/distributions", pageKey: "platformScheduledReportDistribution" },
          // Explicit reportArchive View only — do not OR asset/risk page keys (leaks Reports nav).
          { id: "report-archive", title: t("nav.reports.archive"), url: "/reports/archive", pageKey: "reportArchive" },
        ],
      },
      {
        id: "asset-inventory",
        section: "riskAssets",
        title: t("nav.assetInventory.title"),
        url: "#",
        icon: Package,
        helperKey: "assetInventory",
        focusModules: [MODULES.ASSET],
        items: [
          { id: "asset-group", title: t("nav.assetInventory.assetGroup"), url: "/asset-inventory-management/asset-group" },
          { id: "categories", title: t("nav.assetInventory.categories"), url: "/asset-inventory-management/asset-category" },
          { id: "assets", title: t("nav.assetInventory.assets"), url: "/asset-inventory-management/assets" },
          {
            id: "asset-lifecycle",
            title: t("nav.assetInventory.lifecycleGroup", { defaultValue: "Lifecycle" }),
            url: "#",
            items: [
              { id: "license-management", title: t("nav.assetInventory.licenseManagement"), url: "/asset-inventory-management/license-management" },
              { id: "maintenance-management", title: t("nav.assetInventory.maintenanceManagement"), url: "/asset-inventory-management/maintenance-management" },
              { id: "disposal-management", title: t("nav.assetInventory.disposalManagement"), url: "/asset-inventory-management/disposal-management" },
              { id: "asset-lifecycle-events", title: t("nav.assetInventory.lifecycleEvents"), url: "/asset-inventory-management/asset-lifecycle-events" },
            ],
          },
        ],
      },
      {
        id: "threat-vulnerability",
        section: "riskAssets",
        title: t("nav.threatVulnerability.title", { defaultValue: "Threat and Vulnerability" }),
        url: "#",
        icon: ShieldAlert,
        helperKey: "threatVulnerability",
        focusModules: [MODULES.SCF, MODULES.ISO27001],
        items: [
          { id: "threat-intelligence", title: t("nav.internalControl.threatIntelligence"), url: "/isms-controls/threat-intelligence", permissionKey: "ismsControlsThreatIntelligence" },
          { id: "vulnerability", title: t("nav.internalControl.vulnerability"), url: "/isms-controls/vulnerability", permissionKey: "ismsControlsVulnerability" },
        ],
      },
      {
        id: "risk-operations",
        section: "riskAssets",
        title: t("nav.riskAnalysisOperations.title"),
        url: "#",
        icon: AlertTriangle,
        helperKey: "riskAnalysis",
        focusModules: [MODULES.RISK],
        items: [
          { id: "risk-monitoring", title: t("nav.riskAnalysisOperations.riskMonitoring"), url: "/risk-management/risk-monitoring" },
          { id: "risk-register", title: t("nav.riskAnalysisOperations.riskRegister"), url: "/risk-management/risk-register" },
          { id: "risk-treatment", title: t("nav.riskAnalysisOperations.riskTreatment"), url: "/risk-management/risk-treatment" },
          { id: "residual-risk-acceptance", title: t("nav.riskAnalysisOperations.residualRiskAcceptance"), url: "/risk-management/residual-risk-acceptance", permissionKey: "riskOperationsResidualRiskAcceptance" },
          { id: "risk-opportunities", title: t("nav.riskAnalysisOperations.riskOpportunities"), url: "/risk-management/risk-opportunities", permissionKey: "riskOperationsRiskOpportunities" },
        ],
      },
      {
        id: "vendor-management",
        section: "riskAssets",
        title: t("nav.vendorManagement.title"),
        url: "#",
        icon: ShoppingCart,
        helperKey: "vendorManagement",
        focusModules: [MODULES.ASSET],
        items: [
          { id: "vendor-registry", title: t("nav.vendorManagement.registry"), url: "/vendor-management/vendors" },
          {
            id: "vendor-assessments",
            title: t("nav.vendorManagement.assessments"),
            url: "/vendor-management/vendor-assessments",
            activePathPrefixes: ["/vendor-management/vendor-assessments"],
          },
        ],
      },
      {
        id: "internal-control",
        section: "compliance",
        title: t("nav.internalControl.title"),
        url: "#",
        icon: CheckCircle,
        helperKey: "internalControl",
        focusModules: [MODULES.SCF, MODULES.ISO27001],
        items: [
          { id: "controls", title: t("nav.internalControl.controls"), url: "/isms-controls/control" },
          { id: "soa", title: t("nav.internalControl.soa"), url: "/isms-controls/soa", permissionKey: "ismsControlsSoa" },
        ],
      },
      {
        id: "internal-audit",
        section: "compliance",
        title: t("nav.internalAudit.title"),
        url: "#",
        icon: Search,
        helperKey: "internalAudit",
        focusModules: [MODULES.AUDIT],
        items: [
          { id: "kpi-dashboard", title: t("nav.internalAudit.kpiDashboard"), url: "/audit-compliance/kpi-dashboard", permissionKey: "auditComplianceKpiDashboard" },
          { id: "assessment-questions", title: t("nav.internalAudit.assessmentQuestions"), url: "/isms-controls/assessment-questions" },
          { id: "audit-calendar", title: t("nav.internalAudit.auditCalendar"), url: "/audit-compliance/audit-program" },
          { id: "my-audits", title: t("nav.internalAudit.myAudits"), url: "/audit-compliance/my-audits" },
          { id: "assessment", title: t("nav.internalAudit.assessment"), url: "/audit-compliance/audit-assessment" },
          { id: "capa", title: t("nav.internalAudit.capa"), url: "/audit-compliance/capa" },
        ],
      },
      {
        id: "bigdes-operations",
        section: "compliance",
        title: t("nav.bigdesOperations.title"),
        url: "#",
        icon: Table,
        helperKey: "bigdesOperations",
        focusModules: [MODULES.BIGDES],
        items: [
          { id: "definitions", title: t("nav.bigdesOperations.definitions"), url: "/bigdes-operations/definitions" },
          { id: "asset-group-criticality", title: t("nav.bigdesOperations.assetGroupCriticality"), url: "/bigdes-operations/asset-group-criticality-degree-determination" },
          { id: "measure-applicability", title: t("nav.bigdesOperations.measureApplicability"), url: "/bigdes-operations/measure-applicability" },
          { id: "current-situation-analysis", title: t("nav.bigdesOperations.currentSituationAnalysis"), url: "/bigdes-operations/current-situation-analysis" },
          { id: "road-map", title: t("nav.bigdesOperations.roadMap"), url: "/bigdes-operations/road-map" },
          { id: "compensatory-control", title: t("nav.bigdesOperations.compensatoryControl"), url: "/bigdes-operations/compensatory-control" },
          { id: "audit", title: t("nav.bigdesOperations.audit"), url: "/bigdes-operations/audit" },
        ],
      },
      {
        id: "incident-management",
        section: "resilience",
        title: t("nav.incidentManagement.title"),
        url: "#",
        icon: AlertCircle,
        helperKey: "incidentManagement",
        focusModules: [MODULES.INCIDENT],
        items: [
          { id: "incident-monitoring", title: t("nav.incidentManagement.monitoring"), url: "/incident-management/incidents/monitoring", permissionKey: "incidentManagementMonitoring" },
          { id: "incidents", title: t("nav.incidentManagement.incidents"), url: "/incident-management/incidents", excludePathPrefixes: ["/incident-management/incidents/monitoring"], permissionKey: "incidentManagementIncidents" },
          { id: "actions", title: t("nav.incidentManagement.actions", { defaultValue: "Incident Actions" }), url: "/incident-management/actions", permissionKey: "incidentActions" },
          { id: "incident-templates", title: t("nav.incidentManagement.templates"), url: "/incident-management/templates", permissionKey: "incidentManagementIncidents" },
        ],
      },
      {
        id: "bcm",
        section: "resilience",
        title: t("nav.bcm.title"),
        url: "#",
        icon: Activity,
        helperKey: "bcm",
        focusModules: [MODULES.BCM],
        items: [
          { id: "bia", title: t("nav.bcm.bia"), url: "/bcm/bia", permissionKey: "bcmBia" },
          { id: "bcp", title: t("nav.bcm.bcp"), url: "/bcm/bcp", permissionKey: "bcmBcp" },
          { id: "crisis-event", title: t("nav.bcm.crisisEvent"), url: "/bcm/crisis-event", permissionKey: "bcmCrisisEvent" },
          { id: "exercises", title: t("nav.bcm.exercises"), url: "/bcm/exercises", permissionKey: "bcmExercises" },
        ],
      },
      {
        id: "gdpr-operations",
        section: "privacy",
        title: t("nav.gdprOperations.title"),
        url: "#",
        icon: Lock,
        helperKey: "gdprOperations",
        focusModules: [MODULES.KVKK, MODULES.GDPR],
        items: [
          { id: "gdpr-dashboard", title: t("nav.gdprOperations.dashboard"), url: "/gdpr-operations/dashboard", pageKey: "gdprDashboard" },
          {
            id: "privacy-inventory-compliance",
            title: t("nav.gdprOperations.groups.inventoryCompliance", { defaultValue: "Inventory and Compliance" }),
            url: "#",
            items: [
              { id: "pd-inventory", title: t("nav.assetInventory.pdInventory"), url: "/asset-inventory-management/pd-inventory", pageKey: "assetInventoryPdInventory" },
              { id: "verbis", title: t("nav.gdprOperations.verbis"), url: "/gdpr-operations/verbis", pageKey: "gdprVerbis" },
              { id: "dpia-assessment", title: t("nav.gdprOperations.dpiaAssessment"), url: "/gdpr-operations/dpia-assessment", pageKey: "gdprDpiaAssessment" },
              { id: "disposal", title: t("nav.gdprOperations.disposal"), url: "/gdpr-operations/disposal", pageKey: "gdprDisposal" },
            ],
          },
          {
            id: "privacy-transparency-consent",
            title: t("nav.gdprOperations.groups.transparencyConsent", { defaultValue: "Transparency and Consent" }),
            url: "#",
            items: [
              { id: "privacy-notice", title: t("nav.gdprOperations.privacyNotice"), url: "/gdpr-operations/privacy-notice", pageKey: "gdprPrivacyNotice" },
              { id: "obtaining-explicit-consent", title: t("nav.gdprOperations.obtainingExplicitConsent"), url: "/gdpr-operations/obtaining-explicit-consent", pageKey: "gdprObtainingExplicitConsent" },
              { id: "consent-record", title: t("nav.gdprOperations.consentRecord"), url: "/gdpr-operations/consent-record", pageKey: "gdprConsentRecord" },
              { id: "cookie-inventory", title: t("nav.gdprOperations.cookieInventory"), url: "/gdpr-operations/cookie-inventory", pageKey: "gdprCookieInventory" },
              { id: "dpa-registry", title: t("nav.gdprOperations.dpaRegistry"), url: "/gdpr-operations/dpa-registry", pageKey: "gdprDpa" },
            ],
          },
          {
            id: "privacy-subject-breach",
            title: t("nav.gdprOperations.groups.subjectBreach", { defaultValue: "Data Subject and Breach" }),
            url: "#",
            items: [
              { id: "application-tracking", title: t("nav.gdprOperations.applicationTracking"), url: "/gdpr-operations/application-tracking", pageKey: "gdprApplicationTracking" },
              { id: "data-subject-request", title: t("nav.gdprOperations.dataSubjectRequest"), url: "/gdpr-operations/data-subject-request", pageKey: "gdprDataSubjectRequest" },
              { id: "data-breach", title: t("nav.gdprOperations.dataBreach"), url: "/gdpr-operations/data-breach", pageKey: "gdprDataBreach" },
            ],
          },
        ],
      },
      {
        id: "ticketing",
        section: "serviceManagement",
        title: t("nav.ticketing.title"),
        url: "#",
        icon: Ticket,
        helperKey: "ticketing",
        focusModules: [MODULES.TICKETING],
        items: [
          { id: "ticket-dashboard", title: t("nav.ticketing.dashboard"), url: "/ticketing/dashboard" },
          { id: "ticket-list", title: t("nav.ticketing.ticketList"), url: "/ticketing", exact: true },
          { id: "ticket-my-tasks", title: t("nav.ticketing.ticketMyTasks"), url: "/ticketing/my-tasks" },
          { id: "ticket-templates", title: t("nav.ticketing.ticketTemplates"), url: "/ticketing/templates" },
          {
            id: "ticket-settings",
            title: t("nav.ticketing.ticketSettings"),
            url: "/ticketing/settings",
            items: [
              {
                id: "ticket-settings-general",
                title: t("nav.ticketing.ticketSettingsGeneral"),
                url: "/ticketing/settings?tab=general",
                activeSearchParams: { tab: "general" },
                activeSearchParamsAllowMissing: true,
              },
              {
                id: "ticket-settings-categories",
                title: t("nav.ticketing.ticketSettingsCategories"),
                url: "/ticketing/settings?tab=categories",
                activeSearchParams: { tab: "categories" },
              },
              {
                id: "ticket-settings-sla",
                title: t("nav.ticketing.ticketSettingsSla"),
                url: "/ticketing/settings?tab=sla",
                activeSearchParams: { tab: "sla" },
              },
              {
                id: "ticket-settings-recurrence",
                title: t("nav.ticketing.ticketSettingsRecurrence"),
                url: "/ticketing/settings?tab=recurrence",
                activeSearchParams: { tab: "recurrence" },
                featureKey: "recurringTicketsEnabled",
              },
            ],
          },
        ],
      },
      {
        id: "itsm",
        section: "serviceManagement",
        title: t("nav.itsm.title"),
        url: "#",
        icon: Workflow,
        helperKey: "itsm",
        focusModules: [MODULES.TICKETING],
        items: [
          { id: "itsm-service-catalog", title: t("nav.itsm.serviceCatalog"), url: "/itsm/service-catalog", permissionKey: "itsmServiceCatalog" },
          { id: "itsm-change-request", title: t("nav.itsm.changeRequest"), url: "/itsm/change-request", permissionKey: "itsmChangeRequest" },
          { id: "itsm-problem", title: t("nav.itsm.problem"), url: "/itsm/problem", permissionKey: "itsmProblem" },
          { id: "itsm-supplier-contracts", title: t("nav.itsm.supplierContracts"), url: "/itsm/supplier-contracts", permissionKey: "itsmSupplierContracts" },
          { id: "itsm-unified-search", title: t("nav.itsm.unifiedSearch"), url: "/itsm/hub", permissionKey: "itsmUnifiedSearch" },
        ],
      },
      {
        id: "definitions",
        section: "definitions",
        title: t("nav.definitions.title"),
        url: "#",
        icon: Settings,
        helperKey: "definitions",
        items: [
          { id: "organization", title: t("nav.definitions.organization"), url: "#", focusModules: [MODULES.CORE] },
          { id: "scale", title: t("nav.definitions.scale"), url: "#", focusModules: [MODULES.RISK, MODULES.BCM] },
          {
            id: "personal-data",
            focusModules: [MODULES.KVKK, MODULES.GDPR],
            title: t("nav.definitions.personalData"),
            url: "#",
          },
          {
            id: "compliance-policy",
            focusModules: [MODULES.CORE, MODULES.KVKK, MODULES.GDPR],
            title: t("nav.definitions.compliance"),
            url: "#",
          },
          { id: "document", title: t("nav.definitions.document"), url: "#", focusModules: [MODULES.DOCUMENT] },
          {
            id: "platform",
            focusModules: [MODULES.CORE, MODULES.DIRECTORY],
            title: t("nav.definitions.platform"),
            url: "#",
          },
          {
            id: "report-branding",
            focusModules: [MODULES.CORE],
            title: t("nav.definitions.reportBranding"),
            url: "/definitions/report-branding",
            pageKey: "definitionsReportDesign",
          },
        ],
      },
      {
        id: "user-operations",
        section: "administration",
        title: t("nav.userOperations.title"),
        url: "#",
        icon: Users,
        helperKey: "userOperations",
        focusModules: [MODULES.CORE],
        items: [
          { id: "user-list", title: t("nav.userOperations.userList"), url: "/user-operations/user-management" },
          { id: "role", title: t("nav.userOperations.role"), url: "/user-operations/role-management" },
          { id: "my-delegations", title: t("nav.userOperations.myDelegations"), url: "/user-operations/delegations", permissionKey: "userOperationsDelegations" },
          { id: "admin-delegations", title: t("nav.userOperations.adminDelegations", { defaultValue: "All Delegations" }), url: "/admin/delegations", permissionKey: "adminDelegations" },
        ],
      },
      {
        id: "platform-management",
        section: "administration",
        title: t("nav.platformManagement.title"),
        url: "#",
        icon: Server,
        helperKey: "platformManagement",
        focusModules: [MODULES.CORE],
        items: [
          {
            id: "process-templates",
            title: t("nav.platformManagement.groups.processTemplates", { defaultValue: "Process and Templates" }),
            url: "#",
            items: [
              { id: "party-templates", title: t("nav.platformManagement.partyTemplates"), url: "/platform/party-templates", permissionKey: "platformPartyTemplates" },
              { id: "assignment-templates", title: t("nav.platformManagement.assignmentTemplates"), url: "/platform/assignment-templates", permissionKey: "platformAssignmentTemplates" },
              { id: "collection-campaigns", title: t("nav.platformManagement.collectionCampaigns"), url: "/platform/collection-campaigns", permissionKey: "collectionCampaigns" },
              { id: "workflow-templates", title: t("nav.platformManagement.workflowTemplates"), url: "/platform/workflow-templates", permissionKey: "platformWorkflowTemplates" },
              { id: "pending-workflow-operations", title: t("nav.platformManagement.pendingWorkflowOperations"), url: "/admin/pending-workflow-operations", permissionKey: "adminPendingWorkflowOperations" },
              { id: "business-calendar", title: t("nav.platformManagement.businessCalendar"), url: "/platform/business-calendar", permissionKey: "platformBusinessCalendar" },
            ],
          },
          {
            id: "tenant-customization",
            title: t("nav.platformManagement.groups.tenantCustomization", { defaultValue: "Tenant Customization" }),
            url: "#",
            items: [
              { id: "tailorability", title: t("nav.platformManagement.tailorability"), url: "/platform/tailorability", permissionKey: "platformTailorability" },
              { id: "seed-policy", title: t("nav.platformManagement.seedPolicy", { defaultValue: "Required Record Policy" }), url: "/platform/seed-policy", permissionKey: "platformTailorability" },
              { id: "tenant-compliance", title: t("nav.platformManagement.tenantCompliance"), url: "/admin/compliance-frameworks", permissionKey: "adminTenantCompliance" },
            ],
          },
          {
            id: "communications",
            title: t("nav.platformManagement.communications", { defaultValue: "Communications" }),
            url: "#",
            items: [
              {
                id: "communication-channels",
                title: t("nav.platformManagement.communicationChannels", { defaultValue: "Communication Channels" }),
                url: "/platform/communications/channels",
                permissionKey: "communicationProvider",
              },
              {
                id: "communications-mfa",
                title: t("nav.platformManagement.loginMfa", { defaultValue: "Login MFA" }),
                url: "/platform/communications/mfa",
                permissionKey: "platformMfaSettings",
              },
              {
                id: "notification-templates",
                title: t("nav.platformManagement.notificationTemplates", { defaultValue: "Notification Templates" }),
                url: "/platform/communications/templates",
                permissionKey: "definitionsGeneralSystemMailTemplate",
              },
              {
                id: "notification-policies",
                title: t("nav.platformManagement.notificationPolicies", { defaultValue: "Notification Policies" }),
                url: "/platform/communications/notification-policies",
                permissionKey: "platformNotificationPolicies",
              },
              {
                id: "communications-delivery-history",
                title: t("nav.platformManagement.deliveryHistory", { defaultValue: "Delivery History" }),
                url: "/platform/communications/delivery-history",
              },
            ],
          },
          {
            id: "integrations",
            title: t("nav.platformManagement.groups.integrations", { defaultValue: "Integrations" }),
            url: "#",
            items: [
              { id: "third-party-applications", title: t("nav.platformManagement.thirdPartyApplications", { defaultValue: "Third-Party Applications" }), url: "/platform/third-party-applications", permissionKey: "platformThirdPartyApplications" },
              { id: "webhooks", title: t("nav.platformManagement.webhooks"), url: "/admin/webhooks", permissionKey: "adminWebhooks" },
            ],
          },
          {
            id: "data-transfer",
            title: t("nav.platformManagement.groups.data", { defaultValue: "Data" }),
            url: "#",
            items: [
              { id: "data-export-import", title: t("nav.platformManagement.dataExportImport"), url: "/admin/data-export-import", permissionKey: "adminDataExportImport" },
            ],
          },
          {
            id: "site-operations",
            title: t("nav.platformManagement.groups.siteOps", { defaultValue: "Site Operations" }),
            url: "#",
            items: [
              { id: "queue-monitor", title: t("nav.platformManagement.queueMonitor"), url: "/admin/queues", permissionKey: "platformQueueMonitor" },
              { id: "dead-letter-queue", title: t("nav.platformManagement.deadLetterQueue"), url: "/admin/dead-letter-queue", permissionKey: "adminDeadLetterQueue" },
              { id: "admin-backups", title: t("nav.platformManagement.backups", { defaultValue: "Backups" }), url: "/admin/backups", permissionKey: "adminBackup" },
              { id: "system-logs", title: t("nav.userOperations.systemLogs"), url: "/user-operations/system-logs" },
              { id: "tenant-onboarding", title: t("nav.platformManagement.tenantOnboarding"), url: "/admin/tenant-onboarding", permissionKey: "adminTenantOnboarding" },
            ],
          },
        ],
      },
      {
        id: "user-manual",
        section: "help",
        title: t("nav.userManual.title", { defaultValue: "User Manual" }),
        url: "#",
        icon: BookOpen,
        helperKey: "userManual",
        focusModules: [MODULES.CORE],
        items: [
          { id: "about-sgrc", title: t("nav.userManual.aboutSgrc", { defaultValue: "About sGRC" }), url: "/user-manual/about-sgrc" },
          { id: "patch-notes", title: t("nav.userManual.patchNotes", { defaultValue: "Patch Notes" }), url: "/user-manual/patch-notes" },
        ],
      },
    ],
    navSecondary: [],
  };

  return attachHeaderTabHubChildren(
    attachDefinitionHubChildren(data, {
      tNav: hubTranslators?.tNav || t,
      tBreadcrumb: hubTranslators?.tBreadcrumb || t,
      tMatrix: hubTranslators?.tMatrix || t,
    }),
    t,
  );
}
